import { DocumentButtons } from "PFApp/components/document_buttons";
import PageBox from "PFComponents/box/page_box";
import InlineCalendar from "PFComponents/calendar/inline_calendar";
import DataTable from "PFComponents/data_table/data_table";
import SimplePagination from "PFComponents/pagination/simple_pagination";
import { useIsCurrentUserPermittedTo } from "PFCore/helpers/use_is_permitted_to";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { PermissionRule } from "PFTypes";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./audit_logs.less";

const AuditLogs = ({ auditLogs, fetchLogs, generateDocument }) => {
  const { t } = useTranslation("admin");

  const [page, setPage] = useState(auditLogs?.meta?.page);

  const [date, setDate] = useState({
    from: moment.utc().subtract(3, "days").startOf("day"),
    until: moment().endOf("day")
  });

  const { formatDateTime, formatDatabaseDateTime } = useDateFormatter();

  const data = auditLogs?.entries.map((item) => ({
    created_at: formatDateTime(moment.utc(item?.created_at)),
    description: item?.description
  }));

  useEffect(() => {
    fetchLogs(page, date);
  }, [page, date]);

  const canExportDocs = useIsCurrentUserPermittedTo(PermissionRule.ExportDocuments);

  return (
    <div className={css.wrapper}>
      <h1>{t("auditLogs.title")}</h1>
      <div className={css.header}>
        {canExportDocs && (
          <DocumentButtons generateDocument={() => generateDocument(page, date)} exportOptions={["csv"]}>
            CSV
          </DocumentButtons>
        )}
        <div className={css.calendars}>
          <InlineCalendar
            label={t("auditLogs.from")}
            selectedDate={formatDatabaseDateTime(date.from)}
            showTime={true}
            maxDate={formatDatabaseDateTime(date.until)}
            style={{ marginRight: 10, minWidth: 190 }}
            handleChange={(from) => {
              setDate({ ...date, from });
              setPage(1);
            }}
            useDateDropdowns={true}
          />
          <InlineCalendar
            label={t("auditLogs.until")}
            selectedDate={formatDatabaseDateTime(date.until)}
            showTime={true}
            minDate={formatDatabaseDateTime(date.from)}
            style={{ minWidth: 190 }}
            handleChange={(until) => {
              setDate({ ...date, until });
              setPage(1);
            }}
            useDateDropdowns={true}
          />
        </div>
      </div>
      <PageBox>
        <DataTable
          columns={[
            {
              headerName: t("auditLogs.date"),
              renderCell: (data) => <span style={{ whiteSpace: "nowrap" }}>{data?.created_at}</span>
            },
            {
              headerName: t("auditLogs.description"),
              renderCell: (data) => <span style={{ whiteSpace: "nowrap" }}>{data?.description}</span>
            }
          ]}
          data={data}
        />
      </PageBox>
      <div className={css.pagination}>
        <SimplePagination
          currentPage={auditLogs?.meta?.page}
          totalPages={auditLogs?.meta?.totalPages}
          handleClick={(newPage) => setPage(newPage)}
        />
      </div>
    </div>
  );
};

AuditLogs.propTypes = {
  auditLogs: PropTypes.shape({
    entries: PropTypes.arrayOf(
      PropTypes.shape({
        created_at: PropTypes.string,
        description: PropTypes.string
      })
    ),
    meta: PropTypes.shape({
      page: PropTypes.number,
      totalPages: PropTypes.number
    })
  }).isRequired,
  fetchLogs: PropTypes.func.isRequired,
  generateDocument: PropTypes.func.isRequired
};

export default AuditLogs;
